import {useGSAP} from '@gsap/react'
import {useWindowSize} from '@uidotdev/usehooks'
import clsx from 'clsx'
import gsap from 'gsap'
import {FC, useRef} from 'react'
import {Button} from '../Button/Button'
import {ContentWidthLimiter} from '../ContentWidthLimiter/ContentWidthLimiter'
import {IHowToPlayItem} from './HowToPlayModal.types'
import CloseIcon from './close.svg'

interface IHowToPlayModalProps {
  isOpen: boolean
  title: string
  items: IHowToPlayItem[]
  onConfirm: () => void
  onClose: () => void
  isGrillQuiz?: boolean
}

export const HowToPlayModal: FC<IHowToPlayModalProps> = ({
  isOpen,
  onClose,
  items,
  onConfirm,
  title,
  isGrillQuiz = false,
}) => {
  const {width} = useWindowSize()
  const screenWidth = width || 0

  const backdropRef = useRef<HTMLDivElement | null>(null)
  const titleRef = useRef<HTMLHeadingElement | null>(null)
  const buttonRef = useRef<HTMLDivElement | null>(null)

  useGSAP(
    () => {
      if (isOpen) {
        const timeline = gsap.timeline({})

        timeline.set(backdropRef.current, {opacity: 0})
        timeline.set('.item', {scale: 0})
        timeline.set(titleRef.current, {scale: 0})
        timeline.set(buttonRef.current, {scale: 0})

        timeline.to(backdropRef.current, {autoAlpha: 1, duration: 0.3})
        timeline.to(titleRef.current, {scale: 1, duration: 0.3, ease: 'back.out'}, '<0.1')
        timeline.to('.item', {scale: 1, duration: 0.3, stagger: 0.1, ease: 'back.out'}, '<0.1')
        timeline.to(
          buttonRef.current,
          {scale: 1, duration: 0.3, delay: 0.3 * (items.length - 1), ease: 'back.out'},
          '<',
        )
      } else {
        const timeline = gsap.timeline({})

        timeline.to(backdropRef.current, {autoAlpha: 0, duration: 0.2})

        timeline.set(backdropRef.current, {opacity: 0})
        timeline.set('.item', {scale: 0})
        timeline.set(titleRef.current, {scale: 0})
        timeline.set(buttonRef.current, {scale: 0})
      }
    },
    {
      dependencies: [isOpen],
    },
  )

  return (
    <div
      id="how-to-play"
      ref={backdropRef}
      className={clsx(
        'pointer-events-auto fixed inset-0 z-50 flex flex-col items-center justify-center text-white opacity-0 backdrop-blur-lg',
        !isGrillQuiz && 'bg-[rgba(7,17,68,0.64)]',
        isGrillQuiz && 'bg-[rgba(48 67 80, 0.64)]',
      )}
    >
      <ContentWidthLimiter
        id="how-to-play-modal"
        classNameInner="items-center justify-center flex flex-col"
      >
        <h1 ref={titleRef} id="title" className="mb-[40px] text-[40px] font-bold uppercase">
          {title}
        </h1>

        <div
          className={clsx(
            'mb-[32px] flex flex-row flex-wrap justify-center gap-[24px]',
            screenWidth < 900 ? 'w-[170%]' : screenWidth < 1100 ? 'w-[140%]' : 'w-full',
          )}
          style={{transform: `scale(${screenWidth < 900 ? 0.7 : screenWidth < 1100 ? 0.9 : 1})`}}
        >
          {items.map((item, index) => (
            <div
              key={index}
              id={`step-${index + 1}`}
              className="item flex max-w-[300px] flex-col items-center rounded-[16px] border-[1px] border-[rgba(255,255,255,0.24)] bg-[rgba(255,255,255,0.12)] px-[16px] pb-[24px] pt-[32px] text-center"
            >
              {item.illustration && (
                <img src={item.illustration} alt={title} className="mb-[16px] max-h-[114px]" />
              )}

              <div
                id={`step-${index + 1}-digit`}
                className="mb-[8px] flex size-[32px] items-center justify-center rounded-full bg-white text-black"
              >
                {index + 1}
              </div>
              <h2
                className="mb-[4px] text-[20px] font-bold uppercase leading-[1.2]"
                id={item.titleId}
              >
                {item.title}
              </h2>
              <p className="font-medium leading-[1.25]" id={item.descriptionId}>
                {item.description}
              </p>
            </div>
          ))}
        </div>

        <Button
          ref={buttonRef}
          id="start-game-btn"
          size="small"
          ignoreMinWidth
          color={isGrillQuiz ? 'orange' : 'purple'}
          onClick={onConfirm}
        >
          Start
        </Button>

        <button
          id="close-btn"
          className="fixed right-[24px] top-[24px] z-50 flex size-[48px] items-center justify-center rounded-full bg-white text-black transition-colors hover:bg-opacity-80"
          onClick={onClose}
        >
          <img src={CloseIcon} alt="Lukk" />
        </button>
      </ContentWidthLimiter>
    </div>
  )
}
